import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete, fetchAllPaginated, fetchSchoolsSimple } from '../services/api';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import Pagination from '../components/Pagination';
import DisciplinasDoPolivalente from '../components/DisciplinasDoPolivalente';
import { classesStatusAluno, ehAlunoMatriculado, rotuloStatusAluno } from '../utils/statusAluno';

function Turmas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const userCity = useEffectiveCity();
  const cityId = userCity?.id || null;

  // Estados
  const [turmas, setTurmas] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [filtroEscola, setFiltroEscola] = useState('');
  const [filtroSerie, setFiltroSerie] = useState('');
  const [filtroTurno, setFiltroTurno] = useState('');
  const [filtroAnoLetivo, setFiltroAnoLetivo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Paginação (renderiza apenas a página atual)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingTurma, setEditingTurma] = useState(null);
  const [formData, setFormData] = useState({
    class_name: '',
    id_school: '',
    school_year: new Date().getFullYear(),
    grade: '',
    shift: '',
    id_teacher: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Disciplinas do Polivalente: o que o backend mandou e o que a coordenação
  // marcou. Seleção `null` = lista padrão da rede.
  const [poliConfig, setPoliConfig] = useState(null);
  const [poliSelecao, setPoliSelecao] = useState(null);
  const [poliAlterado, setPoliAlterado] = useState(false);
  // Abrir outra turma antes da resposta chegar não pode trazer a lista da anterior.
  const poliPedido = useRef(0);

  // Modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [turmaToDelete, setTurmaToDelete] = useState(null);


  // Modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState(null);


  // Calendário de frequência
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Opções de turno e série
  const turnosOptions = ['Manhã', 'Tarde', 'Noite', 'Integral'];
  const seriesOptions = [
    'Multisseriada',
    'Ciclo I EJA', 'Ciclo II EJA', 'Ciclo III EJA', 'Ciclo IV EJA',
    'Berçário I', 'Berçário II', 'Berçário III', 'Berçário IV', 'Berçário V',
    'Inf 1', 'Inf 2', 'Inf 3', 'Inf 4', 'Inf 5',
    '1º Ano', '2º Ano', '3º Ano', '4º Ano', '5º Ano',
    '6º Ano', '7º Ano', '8º Ano', '9º Ano',
    '1º Ano EM', '2º Ano EM', '3º Ano EM'
  ];

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Buscar turmas, escolas, professores e alunos em paralelo
      // O backend já filtra baseado no papel do usuário:
      // - Admin: todas as turmas/escolas
      // - Coordenador/Gestor: turmas/escolas da sua cidade
      // - Gerente: apenas turmas/escolas atribuídas a ele
      const cityParams = cityId ? { city: cityId } : {};
      const [turmasData, escolasData, professoresData, alunosData] = await Promise.all([
        fetchAllPaginated('/classes/', cityParams),
        fetchSchoolsSimple(cityId),
        fetchAllPaginated('/teachers/', cityParams),
        fetchAllPaginated('/students/', cityParams),
      ]);

      setTurmas(turmasData);
      setEscolas(escolasData);
      setProfessores(professoresData);
      setAlunos(alunosData);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar turmas
  const turmasFiltradas = turmas.filter(turma => {
    const matchEscola = !filtroEscola || turma.id_school === parseInt(filtroEscola);
    const matchSerie = !filtroSerie || turma.grade === filtroSerie;
    const matchTurno = !filtroTurno || turma.shift === filtroTurno;
    const matchAnoLetivo = !filtroAnoLetivo || turma.school_year === parseInt(filtroAnoLetivo);
    const matchSearch = !searchTerm ||
      turma.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      turma.school_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchEscola && matchSerie && matchTurno && matchAnoLetivo && matchSearch;
  });

  // Anos letivos únicos para filtro
  const anosLetivos = [...new Set(turmas.map(t => t.school_year))].filter(Boolean).sort((a, b) => b - a);

  // Professores disponíveis para vincular à turma (já filtrados por cidade pelo backend)
  const professoresOptions = [...professores]
    .sort((a, b) => (a.teacher_name || '').localeCompare(b.teacher_name || ''))
    .map(p => ({ value: p.id, label: p.teacher_name || `Professor #${p.id}` }));

  // Volta à primeira página quando os filtros mudam
  useEffect(() => {
    setPagina(1);
  }, [filtroEscola, filtroSerie, filtroTurno, filtroAnoLetivo, searchTerm, porPagina]);

  // Apenas a fatia da página atual vai para o DOM
  const turmasPaginadas = turmasFiltradas.slice(
    (pagina - 1) * porPagina,
    pagina * porPagina
  );

  // Contar alunos por turma
  const getAlunosByTurma = (turmaId) => {
    return alunos.filter(a => a.id_class === turmaId);
  };

  // Calcular total de alunos
  const totalAlunos = turmasFiltradas.reduce((acc, turma) => {
    return acc + getAlunosByTurma(turma.id).length;
  }, 0);

  // Permissões baseadas no papel do usuário:
  // - Admin/Coordenador: podem criar, editar e excluir
  // - Supervisor: pode editar turmas das escolas atribuídas (não pode criar/excluir)
  // - Diretor(a): só visualiza o cadastro — altera só a situação dos alunos
  // - Gestor / Referência Técnica / Professor: apenas visualização (read-only)
  //   Professor pode acessar o Diário/Frequência das suas turmas mas não edita o cadastro.
  const canCreate = user?.role === ROLES.ADMIN || user?.role === ROLES.COORDENADOR || user?.role === ROLES.REFERENCIA_TECNICA;
  const canEdit = user?.role === ROLES.ADMIN
    || user?.role === ROLES.COORDENADOR
    || user?.role === ROLES.SUPERVISOR
    || user?.role === ROLES.REFERENCIA_TECNICA;
  const canDelete = user?.role === ROLES.ADMIN || user?.role === ROLES.COORDENADOR;
  // Quem pode ABRIR diário/frequência (mesmo que somente leitura).
  // A página interna controla edição: Admin e Professor escrevem,
  // Coord/Supervisor/RT/Gestor visualizam.
  const canOpenDiaryAttendance = user?.role !== ROLES.GESTOR;

  // Disciplinas do Polivalente: aparecem nos Anos Iniciais (1º ao 5º ano),
  // já no cadastro da turma, e em qualquer turma que tenha Polivalente na
  // grade ou lista escolhida. Turma sem Polivalente (6º ao 9º ano, por
  // exemplo) não tem o que escolher aqui.
  const anosIniciais = /^[1-5]º Ano$/.test(formData.grade || '');
  const mostrarPolivalente = Boolean(poliConfig?.polivalente)
    && (anosIniciais || poliConfig.tem_polivalente || poliConfig.personalizada);

  // Disciplina que não existe no cadastro da rede: a coordenação cria daqui
  // mesmo, e ela entra nas opções para acrescentar.
  const criarDisciplinaDoPolivalente = async (nome) => {
    const nova = await apiPost('/subjects/', { subject_name: nome });
    const item = { id: nova.id, subject_name: nova.subject_name };
    setPoliConfig((cfg) => (cfg ? {
      ...cfg,
      outras: [...cfg.outras, item].sort((x, y) => x.subject_name.localeCompare(y.subject_name, 'pt-BR')),
    } : cfg));
    return item;
  };

  const resetPolivalente = () => {
    poliPedido.current += 1;
    setPoliConfig(null);
    setPoliSelecao(null);
    setPoliAlterado(false);
  };

  // Sem `turmaId` é a turma nova: vem a lista padrão, para já escolher no
  // cadastro; a escolha é gravada depois de criar a turma.
  const carregarPolivalente = (turmaId) => {
    resetPolivalente();
    const pedido = poliPedido.current;
    apiGet(turmaId ? `/classes/${turmaId}/polivalente/` : '/classes/polivalente-padrao/')
      .then((cfg) => {
        if (pedido !== poliPedido.current) return;
        setPoliConfig(cfg);
        setPoliSelecao(
          cfg.personalizada ? cfg.opcoes.filter((o) => o.marcada).map((o) => o.id) : null
        );
      })
      // Sem a lista, a seção só não aparece: o resto do cadastro segue.
      .catch((err) => console.error('Erro ao carregar disciplinas do Polivalente:', err));
  };

  // Abrir modal para criar nova turma
  const handleCreate = () => {
    resetPolivalente();
    setEditingTurma(null);
    setFormData({
      class_name: '',
      id_school: escolas.length === 1 ? escolas[0].id : '',
      school_year: new Date().getFullYear(),
      grade: '',
      shift: '',
      id_teacher: '',
    });
    setFormErrors({});
    setShowModal(true);
    carregarPolivalente(null);
  };

  // Abrir modal para editar turma
  const handleEdit = (turma) => {
    setEditingTurma(turma);
    setFormData({
      class_name: turma.class_name || '',
      id_school: turma.id_school || '',
      school_year: turma.school_year || new Date().getFullYear(),
      grade: turma.grade || '',
      shift: turma.shift || '',
      id_teacher: turma.id_teacher || '',
    });
    setFormErrors({});
    setShowModal(true);
    carregarPolivalente(turma.id);
  };

  // Buscar datas com frequência registrada para uma turma
  const fetchAttendanceDates = useCallback(async (classId) => {
    setLoadingCalendar(true);
    try {
      const response = await apiGet(`/student-attendance/?id_class=${classId}`);
      const records = response.results || response || [];
      // Extrair datas únicas
      const uniqueDates = [...new Set(records.map(r => r.attendance_date))];
      setAttendanceDates(uniqueDates);
    } catch (err) {
      console.error('Erro ao buscar datas de frequência:', err);
      setAttendanceDates([]);
    } finally {
      setLoadingCalendar(false);
    }
  }, []);

  // Funções auxiliares do calendário
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDateForComparison = (day) => {
    const month = String(calendarMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${calendarYear}-${month}-${dayStr}`;
  };

  const isDateWithAttendance = (day) => {
    const dateStr = formatDateForComparison(day);
    return attendanceDates.includes(dateStr);
  };

  const handleCalendarDateClick = (day) => {
    if (!selectedTurma || getAlunosByTurma(selectedTurma.id).length === 0) return;

    // Abre a Frequência do Diário já na data clicada.
    navigate(`/turmas/${selectedTurma.id}/diario?tab=frequencia&data=${formatDateForComparison(day)}`);
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Abrir modal de detalhes
  const handleViewDetails = async (turma) => {
    setSelectedTurma(turma);
    setCalendarMonth(new Date().getMonth());
    setCalendarYear(new Date().getFullYear());
    setShowDetailsModal(true);
    await fetchAttendanceDates(turma.id);
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};
    if (!formData.class_name.trim()) errors.class_name = 'Nome da turma é obrigatório';
    if (!formData.id_school) errors.id_school = 'Escola é obrigatória';
    if (!formData.school_year) errors.school_year = 'Ano letivo é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar turma (criar ou atualizar)
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        id_school: parseInt(formData.id_school),
        school_year: parseInt(formData.school_year),
        // Professor é opcional: vazio vira null (turma sem regente definido)
        id_teacher: formData.id_teacher ? parseInt(formData.id_teacher) : null,
      };

      let turmaId = editingTurma?.id;
      if (editingTurma) {
        await apiPut(`/classes/${editingTurma.id}/`, dataToSend);
      } else {
        const criada = await apiPost('/classes/', dataToSend);
        turmaId = criada?.id;
      }

      if (turmaId && mostrarPolivalente && poliAlterado) {
        try {
          await apiPut(`/classes/${turmaId}/polivalente/`, { disciplinas: poliSelecao });
        } catch (err) {
          // A turma nova já foi criada: sem isto, salvar de novo a duplicaria.
          if (!editingTurma) {
            setShowModal(false);
            fetchData();
            alert(`A turma foi criada, mas as Disciplinas do Polivalente não foram gravadas (${err.message}). Abra a turma em Editar para escolher de novo.`);
            return;
          }
          throw new Error(`Disciplinas do Polivalente: ${err.message}`);
        }
        setPoliAlterado(false);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar turma:', err);
      setFormErrors({ submit: err.message || 'Erro ao salvar turma' });
    } finally {
      setSaving(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (turma) => {
    setTurmaToDelete(turma);
    setShowDeleteModal(true);
  };

  const handleDelete = async (turma) => {
    await apiDelete(`/classes/${turma.id}/`);
    await fetchData();
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltroEscola('');
    setFiltroSerie('');
    setFiltroTurno('');
    setFiltroAnoLetivo('');
    setSearchTerm('');
  };

  // A chamada deixou de ser uma tela solta: é a aba "Frequência" do Diário,
  // para o professor não ter dois lugares diferentes de lançar frequência da
  // mesma turma.
  const handleOpenAttendance = (turma) => {
    navigate(`/turmas/${turma.id}/diario?tab=frequencia`);
  };

  // Navegar para página do diário da turma
  const handleOpenDiary = (turma) => {
    navigate(`/turmas/${turma.id}/diario`);
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Turmas..."
        subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error}</p>
          <button
            onClick={fetchData}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Turmas
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Gerencie as turmas do sistema
            {userCity && <span className="text-sideb-blue"> - {userCity.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          {canCreate && (
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Nova Turma
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Busca */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Buscar turma ou escola..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />
          </div>

          {/* Filtro por Escola */}
          <SearchableSelect
            value={filtroEscola}
            onChange={setFiltroEscola}
            options={escolas.map(escola => ({ value: escola.id, label: escola.school }))}
            emptyOptionLabel="Todas as escolas"
            darkMode={darkMode}
            className="w-full min-w-0"
          />

          {/* Filtro por Série */}
          <SearchableSelect
            value={filtroSerie}
            onChange={setFiltroSerie}
            options={seriesOptions}
            emptyOptionLabel="Todas as séries"
            darkMode={darkMode}
            className="w-full min-w-0"
          />

          {/* Filtro por Turno */}
          <SearchableSelect
            value={filtroTurno}
            onChange={setFiltroTurno}
            options={turnosOptions}
            emptyOptionLabel="Todos os turnos"
            darkMode={darkMode}
            className="w-full min-w-0"
          />

          {/* Filtro por Ano Letivo */}
          <SearchableSelect
            value={filtroAnoLetivo}
            onChange={setFiltroAnoLetivo}
            options={anosLetivos}
            emptyOptionLabel="Todos os anos"
            darkMode={darkMode}
            className="w-full min-w-0"
          />
        </div>

        {/* Limpar filtros */}
        {(filtroEscola || filtroSerie || filtroTurno || filtroAnoLetivo || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className={`text-sm ${darkMode ? 'text-[#0974BA] hover:text-[#0974BA]' : 'text-[#0974BA] hover:text-[#0974BA]'}`}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {turmas.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Turmas</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {turmasFiltradas.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turmas Filtradas</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {totalAlunos}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Alunos</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {escolas.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Escolas</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {[...new Set(turmas.map(t => t.school_year))].length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Anos Letivos</div>
        </div>
      </div>

      {/* Lista de Turmas */}
      {turmasFiltradas.length === 0 ? (
        <div className={`rounded-xl p-12 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-5xl mb-4">📚</div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Nenhuma turma encontrada
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {turmas.length === 0
              ? 'Não há turmas cadastradas ainda.'
              : 'Nenhuma turma corresponde aos filtros aplicados.'}
          </p>
        </div>
      ) : (
        <div className={`rounded-xl overflow-hidden ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Turma
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Escola
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Série
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Turno
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Ano Letivo
                  </th>
                  <th className={`text-center px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Alunos
                  </th>
                  {(canEdit || canDelete) && (
                    <th className={`text-right px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                {turmasPaginadas.map(turma => (
                  <tr
                    key={turma.id}
                    className={`transition-colors ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                  >
                    <td className={`px-6 py-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      <span className="font-medium">{turma.class_name}</span>
                    </td>
                    <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {turma.school_name || '-'}
                    </td>
                    <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {turma.grade || '-'}
                    </td>
                    <td className={`px-6 py-4`}>
                      {turma.shift ? (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${turma.shift === 'Manhã' ? 'bg-[#F9B047]/20 text-[#F9B047]' :
                          turma.shift === 'Tarde' ? 'bg-[#F9B047]/20 text-[#F9B047]' :
                            turma.shift === 'Noite' ? 'bg-[#2E4974]/20 text-[#2E4974]' :
                              'bg-[#0974BA]/20 text-[#0974BA]'
                          }`}>
                          {turma.shift}
                        </span>
                      ) : '-'}
                    </td>
                    <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {turma.school_year || '-'}
                    </td>
                    <td className={`px-6 py-4 text-center`}>
                      <button
                        onClick={() => handleViewDetails(turma)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium ${darkMode
                          ? 'bg-sideb-blue/20 text-sideb-blue hover:bg-sideb-blue/30'
                          : 'bg-[#0974BA]/10 text-[#0974BA] hover:bg-[#0974BA]/20'
                          }`}
                      >
                        {getAlunosByTurma(turma.id).length} alunos
                      </button>
                    </td>
                    {(canEdit || canDelete || canOpenDiaryAttendance) && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {canOpenDiaryAttendance && (
                            <button
                              onClick={() => handleOpenAttendance(turma)}
                              disabled={getAlunosByTurma(turma.id).length === 0}
                              className={`p-2 rounded-lg transition-colors ${getAlunosByTurma(turma.id).length === 0
                                ? darkMode
                                  ? 'text-slate-600 cursor-not-allowed'
                                  : 'text-slate-300 cursor-not-allowed'
                                : darkMode
                                  ? 'hover:bg-[#13983D]/20 text-[#13983D] hover:text-[#13983D]'
                                  : 'hover:bg-[#13983D]/10 text-[#13983D] hover:text-[#13983D]'
                                }`}
                              title={getAlunosByTurma(turma.id).length === 0 ? 'Sem alunos matriculados' : 'Registrar Frequência'}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                          {canOpenDiaryAttendance && (
                            <button
                              onClick={() => handleOpenDiary(turma)}
                              disabled={getAlunosByTurma(turma.id).length === 0}
                              className={`p-2 rounded-lg transition-colors ${getAlunosByTurma(turma.id).length === 0
                                ? darkMode
                                  ? 'text-slate-600 cursor-not-allowed'
                                  : 'text-slate-300 cursor-not-allowed'
                                : darkMode
                                  ? 'hover:bg-[#0974BA]/20 text-[#0974BA] hover:text-[#0974BA]'
                                  : 'hover:bg-[#0974BA]/10 text-[#0974BA] hover:text-[#0974BA]'
                                }`}
                              title={getAlunosByTurma(turma.id).length === 0 ? 'Sem alunos matriculados' : 'Diário da Turma'}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                              </svg>
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(turma)}
                              className={`p-2 rounded-lg transition-colors ${darkMode
                                ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                                }`}
                              title="Editar"
                            >
                              ✏️
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteClick(turma)}
                              className={`p-2 rounded-lg transition-colors ${darkMode
                                ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                                : 'hover:bg-[#E83A3C]/10 text-slate-500 hover:text-[#E83A3C]'
                                }`}
                              title="Excluir"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-4">
            <Pagination
              paginaAtual={pagina}
              totalItens={turmasFiltradas.length}
              itensPorPagina={porPagina}
              onPageChange={setPagina}
              onPageSizeChange={setPorPagina}
              darkMode={darkMode}
              rotuloItens="turmas"
            />
          </div>
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {editingTurma ? 'Editar Turma' : 'Nova Turma'}
            </h2>

            <div className="space-y-4">
              {/* Nome da Turma */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome da Turma *
                </label>
                <input
                  type="text"
                  value={formData.class_name}
                  onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.class_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: 5º Ano A"
                />
                {formErrors.class_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.class_name}</p>
                )}
              </div>

              {/* Escola */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Escola *
                </label>
                <SearchableSelect
                  value={formData.id_school}
                  onChange={(v) => setFormData({ ...formData, id_school: v })}
                  options={escolas.map(escola => ({ value: escola.id, label: escola.school }))}
                  placeholder="Selecione uma escola"
                  darkMode={darkMode}
                  required
                  className={`w-full ${formErrors.id_school ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                />
                {formErrors.id_school && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.id_school}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Ano Letivo */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Ano Letivo *
                  </label>
                  <input
                    type="number"
                    value={formData.school_year}
                    onChange={(e) => setFormData({ ...formData, school_year: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.school_year ? 'border-[#E83A3C]' : ''}`}
                    min="2020"
                    max="2030"
                  />
                </div>

                {/* Série */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Série
                  </label>
                  <SearchableSelect
                    value={formData.grade}
                    onChange={(v) => setFormData({ ...formData, grade: v })}
                    options={seriesOptions}
                    placeholder="Selecione"
                    darkMode={darkMode}
                    className="w-full"
                  />
                </div>

                {/* Turno */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Turno
                  </label>
                  <SearchableSelect
                    value={formData.shift}
                    onChange={(v) => setFormData({ ...formData, shift: v })}
                    options={turnosOptions}
                    placeholder="Selecione"
                    darkMode={darkMode}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Professor (regente da turma) */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Professor
                </label>
                <SearchableSelect
                  value={formData.id_teacher}
                  onChange={(v) => setFormData({ ...formData, id_teacher: v })}
                  options={professoresOptions}
                  emptyOptionLabel="Sem professor definido"
                  placeholder="Selecione um professor"
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>

              {mostrarPolivalente && (
                <DisciplinasDoPolivalente
                  config={poliConfig}
                  selecao={poliSelecao}
                  onChange={(nova) => {
                    setPoliSelecao(nova);
                    setPoliAlterado(true);
                  }}
                  onCriarDisciplina={canCreate ? criarDisciplinaDoPolivalente : undefined}
                  darkMode={darkMode}
                />
              )}

              {formErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{formErrors.submit}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                  }`}
              >
                {saving ? 'Salvando...' : editingTurma ? 'Salvar Alterações' : 'Criar Turma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTurmaToDelete(null);
        }}
        onConfirm={handleDelete}
        item={turmaToDelete}
        itemName={turmaToDelete?.class_name}
        itemType="Turma"
      />

      {/* Modal de Detalhes da Turma */}
      {showDetailsModal && selectedTurma && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {selectedTurma.class_name}
                </h2>
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {selectedTurma.school_name}
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-700 text-slate-400'
                  : 'hover:bg-slate-100 text-slate-500'
                  }`}
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Informações da Turma */}
              <div>
                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Informações da Turma
                </h3>
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Série</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedTurma.grade || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Turno</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedTurma.shift || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Ano Letivo</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedTurma.school_year || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estatísticas */}
              <div>
                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Estatísticas
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`rounded-xl p-4 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                      {getAlunosByTurma(selectedTurma.id).length}
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Alunos</p>
                  </div>
                  <div className={`rounded-xl p-4 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                      {getAlunosByTurma(selectedTurma.id).filter(a => ehAlunoMatriculado(a.status)).length}
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alunos Ativos</p>
                  </div>
                </div>
              </div>

              {/* Calendário de Frequência */}
              {canEdit && getAlunosByTurma(selectedTurma.id).length > 0 && (
                <div>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Calendário de Frequência
                  </h3>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Clique em uma data para registrar ou editar a frequência. Datas em verde já possuem frequência registrada.
                  </p>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    {/* Navegação do mês */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => {
                          if (calendarMonth === 0) {
                            setCalendarMonth(11);
                            setCalendarYear(calendarYear - 1);
                          } else {
                            setCalendarMonth(calendarMonth - 1);
                          }
                        }}
                        className={`p-2 rounded-lg transition-colors ${darkMode
                          ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                          : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        ←
                      </button>
                      <span className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {monthNames[calendarMonth]} {calendarYear}
                      </span>
                      <button
                        onClick={() => {
                          if (calendarMonth === 11) {
                            setCalendarMonth(0);
                            setCalendarYear(calendarYear + 1);
                          } else {
                            setCalendarMonth(calendarMonth + 1);
                          }
                        }}
                        className={`p-2 rounded-lg transition-colors ${darkMode
                          ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                          : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        →
                      </button>
                    </div>

                    {/* Dias da semana */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekDays.map(day => (
                        <div
                          key={day}
                          className={`text-center text-xs font-semibold py-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}
                        >
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Dias do mês */}
                    {loadingCalendar ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-7 gap-1">
                        {/* Espaços vazios para o início do mês */}
                        {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, index) => (
                          <div key={`empty-${index}`} className="aspect-square" />
                        ))}

                        {/* Dias do mês */}
                        {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, index) => {
                          const day = index + 1;
                          const hasAttendance = isDateWithAttendance(day);
                          const isToday =
                            day === new Date().getDate() &&
                            calendarMonth === new Date().getMonth() &&
                            calendarYear === new Date().getFullYear();

                          return (
                            <button
                              key={day}
                              onClick={() => handleCalendarDateClick(day)}
                              className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all hover:scale-105 ${hasAttendance
                                ? 'bg-[#13983D] text-white shadow-lg shadow-[#13983D]/30 hover:bg-[#13983D]'
                                : isToday
                                  ? darkMode
                                    ? 'bg-[#0974BA]/30 text-[#0974BA] ring-2 ring-[#0974BA]'
                                    : 'bg-[#0974BA]/10 text-[#0974BA] ring-2 ring-[#0974BA]'
                                  : darkMode
                                    ? 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              title={hasAttendance ? 'Frequência registrada - clique para editar' : 'Clique para registrar frequência'}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Legenda */}
                    <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-600/30">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-[#13983D]" />
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Com frequência</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${darkMode ? 'bg-slate-600/50' : 'bg-slate-100'}`} />
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sem frequência</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ring-2 ring-[#0974BA] ${darkMode ? 'bg-[#0974BA]/30' : 'bg-[#0974BA]/10'}`} />
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Hoje</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Alunos */}
              {getAlunosByTurma(selectedTurma.id).length > 0 && (
                <div>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Lista de Alunos ({getAlunosByTurma(selectedTurma.id).length})
                  </h3>
                  <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0">
                          <tr className={darkMode ? 'bg-slate-700' : 'bg-slate-100'}>
                            <th className={`text-left px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Nome</th>
                            <th className={`text-left px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Matrícula</th>
                            <th className={`text-center px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Status</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'divide-slate-600' : 'divide-slate-200'}`}>
                          {getAlunosByTurma(selectedTurma.id).map(aluno => (
                            <tr key={aluno.id_student || aluno.id}>
                              <td className={`px-4 py-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                {aluno.student_name}
                              </td>
                              <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                {aluno.student_serial || '-'}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${classesStatusAluno(aluno.status)}`}>
                                  {rotuloStatusAluno(aluno.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <div>
                {canOpenDiaryAttendance && getAlunosByTurma(selectedTurma.id).length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        navigate(`/turmas/${selectedTurma.id}/diario?tab=frequencia`);
                      }}
                      className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25 transition-all"
                    >
                      Frequência
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        navigate(`/turmas/${selectedTurma.id}/diario`);
                      }}
                      className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 transition-all"
                    >
                      Diário da Turma
                    </button>
                    {/* A grade define qual disciplina ocupa cada aula do dia —
                        é o que permite a chamada valer só para aquele horário. */}
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        navigate(`/turmas/${selectedTurma.id}/grade-horaria`);
                      }}
                      className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      Grade Horária
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default Turmas;
